import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { API, axios } from "@/api";
import { X, TrendingUp, Heart, ShieldCheck, ClipboardCheck, Loader2 } from "lucide-react";
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";

const PERIODS = [
  { key: "day", label: "7D" },
  { key: "week", label: "4W" },
  { key: "month", label: "3M" },
  { key: "all", label: "All" },
];

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-slate-800 text-white text-xs rounded-lg px-3 py-2 shadow-lg">
      <p className="font-semibold mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full" style={{ background: p.color }} />
          {p.name}: {p.value}
        </p>
      ))}
    </div>
  );
};

export default function TrendModal({ isOpen, onClose, businessId, businessName }) {
  const [period, setPeriod] = useState("month");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isOpen || !businessId) return;
    setLoading(true);
    axios
      .get(`${API}/offers/${businessId}/trend?period=${period}`)
      .then((res) => setData(res.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [isOpen, businessId, period]);

  if (!isOpen) return null;

  const chartData = data?.data || [];
  const hasData = chartData.some((d) => d.favorites + d.verifications + d.claims > 0);

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[200] bg-black/40 backdrop-blur-sm flex items-center justify-center px-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ y: 300 }}
          animate={{ y: 0 }}
          exit={{ y: 300 }}
          transition={{ type: "spring", damping: 28, stiffness: 300 }}
          className="w-full max-w-lg bg-white rounded-2xl shadow-2xl"
          onClick={(e) => e.stopPropagation()}
          data-testid="trend-modal"
        >
          {/* Handle */}
          <div className="flex justify-center pt-3 pb-1">
            <div className="w-10 h-1 rounded-full bg-slate-200" />
          </div>

          {/* Header */}
          <div className="px-5 pb-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-pink-500" />
              <div>
                <h2 className="text-sm font-bold text-slate-800" data-testid="trend-modal-title">{businessName || "Trend"}</h2>
                <p className="text-[10px] text-slate-400">Activity over time</p>
              </div>
            </div>
            <button onClick={onClose} className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center" data-testid="trend-modal-close">
              <X className="w-4 h-4 text-slate-500" />
            </button>
          </div>

          {/* Period selector */}
          <div className="px-5 pb-3">
            <div className="flex gap-1 bg-slate-100 rounded-xl p-1">
              {PERIODS.map((p) => (
                <button
                  key={p.key}
                  onClick={() => setPeriod(p.key)}
                  className={`flex-1 py-2 text-xs font-semibold rounded-lg transition-all ${
                    period === p.key
                      ? "bg-white text-slate-800 shadow-sm"
                      : "text-slate-400 hover:text-slate-600"
                  }`}
                  data-testid={`trend-period-${p.key}`}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          {/* Chart */}
          <div className="px-3 pb-3" style={{ height: 220 }}>
            {loading ? (
              <div className="h-full flex items-center justify-center">
                <Loader2 className="w-6 h-6 animate-spin text-slate-300" />
              </div>
            ) : !hasData ? (
              <div className="h-full flex flex-col items-center justify-center text-slate-300">
                <TrendingUp className="w-10 h-10 mb-2" />
                <p className="text-sm font-medium">No activity yet for this period</p>
                <p className="text-xs mt-1">Favorites, verifications, and claims will appear here</p>
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                  <defs>
                    <linearGradient id="gFav" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#ec4899" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#ec4899" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="gVer" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="gClm" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 10, fill: "#94a3b8" }}
                    tickFormatter={(v) => {
                      if (v.includes("-W")) return v.split("-W")[1] ? `W${v.split("-W")[1]}` : v;
                      const parts = v.split("-");
                      return parts.length === 3 ? `${parts[1]}/${parts[2]}` : v;
                    }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis tick={{ fontSize: 10, fill: "#94a3b8" }} axisLine={false} tickLine={false} allowDecimals={false} />
                  <Tooltip content={<CustomTooltip />} />
                  <Area type="monotone" dataKey="favorites" name="Favorites" stroke="#ec4899" fill="url(#gFav)" strokeWidth={2} />
                  <Area type="monotone" dataKey="verifications" name="Verifications" stroke="#3b82f6" fill="url(#gVer)" strokeWidth={2} />
                  <Area type="monotone" dataKey="claims" name="Claims" stroke="#10b981" fill="url(#gClm)" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </div>

          {/* Legend */}
          <div className="px-5 pb-8 flex items-center justify-center gap-5">
            <div className="flex items-center gap-1.5">
              <Heart className="w-3 h-3 text-pink-500" />
              <span className="text-[10px] text-slate-500 font-medium">Favorites</span>
            </div>
            <div className="flex items-center gap-1.5">
              <ShieldCheck className="w-3 h-3 text-blue-500" />
              <span className="text-[10px] text-slate-500 font-medium">Verifications</span>
            </div>
            <div className="flex items-center gap-1.5">
              <ClipboardCheck className="w-3 h-3 text-emerald-500" />
              <span className="text-[10px] text-slate-500 font-medium">Claims</span>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
